import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Spinner,
  Text,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { setClinicId as setApiClinicIdValue, setTokenManager } from '../services/api';
import TokenManager from '../services/TokenManager';

const ClinicContext = createContext();

export const useClinic = () => {
  const context = useContext(ClinicContext);
  if (!context) {
    throw new Error('useClinic must be used within a ClinicProvider');
  }
  return context;
};

const getClinicIdFromUrl = () => {
  // Try to get key from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('key') || urlParams.get('clinicId');
};

const validateClinicAccess = async (
  setClinic,
  setError,
  setLoading,
  setApiClinicId,
  setTokenMgr,
) => {
  try {
    const clinicId = getClinicIdFromUrl();

    if (!clinicId) {
      setError({
        title: 'Key Required',
        description: 'This scheduling widget requires a valid key to function properly.',
      });
      setLoading(false);
      return;
    }

    // Get the origin for validation
    const { origin } = window.location;

    // Initialize TokenManager
    const tokenManager = new TokenManager();

    // Request JWT token (this validates clinic access)
    await tokenManager.requestToken(clinicId, origin);

    // Set token manager for API service
    setTokenManager(tokenManager);
    setTokenMgr(tokenManager);

    // Set clinic info (validation call for backward compatibility and clinic details)
    const response = await fetch('/api/validate-clinic', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key: clinicId,
        origin,
      }),
    });

    if (!response.ok) {
      const { message } = await response.json();
      throw new Error(message || 'Clinic validation failed');
    }

    const data = await response.json();

    if (data.status === 'valid') {
      setClinic(data.clinic);
      setApiClinicId(data.clinic.id); // Set clinic ID for API calls
      setError(null);
    } else {
      throw new Error('Invalid clinic access');
    }
  } catch (err) {
    setError({
      title: 'Access Denied',
      description: err.message || 'This website is not authorized to embed the scheduling widget.',
    });
  } finally {
    setLoading(false);
  }
};

function ClinicProvider({ children }) {
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenManager, setTokenMgr] = useState(null);
  const tokenManagerRef = useRef(null);

  useEffect(() => {
    const setTokenMgrWithRef = (tm) => {
      tokenManagerRef.current = tm;
      setTokenMgr(tm);
    };

    validateClinicAccess(setClinic, setError, setLoading, setApiClinicIdValue, setTokenMgrWithRef);

    // Cleanup on unmount - uses ref to access current value
    return () => {
      if (tokenManagerRef.current) {
        tokenManagerRef.current.clearToken();
      }
    };
  }, []);

  const value = useMemo(() => ({
    clinic,
    loading,
    error,
    isValid: !!clinic && !error,
    tokenManager,
  }), [clinic, loading, error, tokenManager]);

  // Loading state
  if (loading) {
    return (
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex="9999"
      >
        <Box textAlign="center">
          <Spinner size="xl" color="#04385D" mb="4" />
          <Text color="gray.600">Validating clinic access...</Text>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p="4"
        zIndex="9999"
      >
        <Alert status="error" maxW="500px" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">{error.title}</Text>
            <Text fontSize="sm">{error.description}</Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <ClinicContext.Provider value={value}>
      {children}
    </ClinicContext.Provider>
  );
}

ClinicProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ClinicProvider;
